/**
 * Where the launcher button and the chat window sit.
 *
 * The launcher is dragged anywhere on the page and stays where it is put
 * between visits. Dropping it near either bottom corner docks it there, so the
 * common case — put it back where it came from — does not have to be pixel
 * accurate, and a docked launcher follows that corner when the window is
 * resized rather than staying at stale coordinates.
 *
 * The chat window is placed against the launcher: on the same side of the
 * screen, above it where there is room and below it where there is not. Both
 * are clamped clear of the app's own header, which the caller measures.
 *
 * All of it is absolute viewport pixels, and all of it is here rather than in
 * the hook so the arithmetic can be tested without a DOM.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export const BTN_SIZE = 60;
export const MARGIN = 20;
/** Between the launcher and the window, so the two do not touch. */
export const GAP = 20;
export const WINDOW_W = 380;
export const WINDOW_H = 500;
/** How near a corner a drop has to land to dock there. */
export const SNAP_THRESHOLD = 110;
/** At or under this the window goes full screen and dragging is off. */
export const MOBILE_BREAKPOINT = 768;

export const STORAGE_KEY = 'derek-chat-launcher-pos';

export type Corner = 'left' | 'right';

/** What is written to storage: a corner to follow, or a fixed spot. */
export type StoredPosition = { corner: Corner } | Point;

export function between(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(value, hi));
}

/** The top-left of the launcher when docked to `corner`. */
export function cornerPos(corner: Corner, viewport: Viewport): Point {
  return {
    x: corner === 'left' ? MARGIN : viewport.width - MARGIN - BTN_SIZE,
    y: viewport.height - MARGIN - BTN_SIZE,
  };
}

export function clampLauncher(point: Point, viewport: Viewport, topBound: number): Point {
  return {
    x: between(point.x, MARGIN, viewport.width - MARGIN - BTN_SIZE),
    y: between(point.y, topBound, viewport.height - MARGIN - BTN_SIZE),
  };
}

/**
 * Where a drop settles: docked to the nearer corner if it landed close enough,
 * otherwise clamped where it is. A null corner means "stay at these
 * coordinates" — a resize then clamps rather than re-docks.
 */
export function settle(
  point: Point,
  viewport: Viewport,
  topBound: number,
): { corner: Corner | null; position: Point } {
  const clamped = clampLauncher(point, viewport, topBound);
  const left = cornerPos('left', viewport);
  const right = cornerPos('right', viewport);
  const toLeft = Math.hypot(clamped.x - left.x, clamped.y - left.y);
  const toRight = Math.hypot(clamped.x - right.x, clamped.y - right.y);
  const nearest: { corner: Corner; distance: number; position: Point } =
    toLeft <= toRight
      ? { corner: 'left', distance: toLeft, position: left }
      : { corner: 'right', distance: toRight, position: right };

  return nearest.distance <= SNAP_THRESHOLD
    ? { corner: nearest.corner, position: nearest.position }
    : { corner: null, position: clamped };
}

/** The top-left of the chat window for a launcher at `launcher`. */
export function windowPosition(launcher: Point, viewport: Viewport, topBound: number): Point {
  // Which half of the screen the launcher is in decides which of its edges the
  // window lines up with, so the window opens inwards rather than off-screen.
  const onLeft = launcher.x + BTN_SIZE / 2 < viewport.width / 2;
  const x = onLeft ? launcher.x : launcher.x + BTN_SIZE - WINDOW_W;
  // Above the launcher by preference; below it when that would run into the
  // header.
  const above = launcher.y - GAP - WINDOW_H;
  const y = above < topBound ? launcher.y + BTN_SIZE + GAP : above;
  return {
    x: between(x, MARGIN, viewport.width - MARGIN - WINDOW_W),
    y: between(y, topBound, viewport.height - MARGIN - WINDOW_H),
  };
}

export function isMobileViewport(viewport: Viewport): boolean {
  return viewport.width <= MOBILE_BREAKPOINT;
}

/** Reads back what `StoredPosition` was written, ignoring anything else. */
export function parseStoredPosition(raw: string | null): StoredPosition | null {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    if (saved?.corner === 'left' || saved?.corner === 'right') return { corner: saved.corner };
    if (typeof saved?.x === 'number' && typeof saved?.y === 'number') {
      return { x: saved.x, y: saved.y };
    }
  } catch {
    // Malformed storage is the same as none at all.
  }
  return null;
}

/** The launcher position a stored value means in the viewport it is read in. */
export function positionFromStored(
  stored: StoredPosition | null,
  viewport: Viewport,
  topBound: number,
): { corner: Corner | null; position: Point } {
  if (stored && 'corner' in stored) {
    return { corner: stored.corner, position: cornerPos(stored.corner, viewport) };
  }
  if (stored) {
    return { corner: null, position: clampLauncher(stored, viewport, topBound) };
  }
  return { corner: 'right', position: cornerPos('right', viewport) };
}
